import { errorNotifier, successNotifier } from "../../../components/NotificationHandler";
import http, { AUTH_ROUTES } from "../../../services/api";

export const fetchStreetsData = async (setStreetsData, setLoadingStreets) => {
  try {
    setLoadingStreets(true);
    const { data } = await http.get("/user/house");
    
    if (!data?.result?.data?.houses || !Array.isArray(data.result.data.houses)) {
      console.error("Invalid data structure received:", data);
      throw new Error("Invalid data structure received from API");
    }

    // Transform the data into a format that allows mapping by street
    const transformedData = data.result.data.houses.reduce((acc, house) => {
      const houseNumber = house?.houseNumber || house?.estate?.[0]?.houseNumber;
      const unit = house?.unit || houseNumber;
      const apartmentType = house?.apartment;
      const shouldShowUnit = unit && String(unit).toUpperCase() !== "EMPTY";
      const shouldShowApartment =
        apartmentType && String(apartmentType).toUpperCase() !== "EMPTY";

      // Validate house data
      if (!house?.streetId?._id || !house?.streetId?.streetName || !houseNumber || !house?._id) {
        console.warn("Skipping invalid house data:", house);
        return acc;
      }

      const { streetId, _id: houseId } = house;
      const { _id: streetIdValue, streetName, streetDescription } = streetId;

      // Ensure each street has an entry with its name and corresponding house details
      if (!acc[streetIdValue]) {
        acc[streetIdValue] = {
          streetName,
          streetId: streetIdValue,
          streetDescription,
          houses: [],
          houseDetails: {} // Add a map of house numbers to unit details
        };
      }

      // Add house number if not already present
      if (!acc[streetIdValue].houses.includes(houseNumber)) {
        acc[streetIdValue].houses.push(houseNumber);
        acc[streetIdValue].houseDetails[houseNumber] = {
          houseId: shouldShowApartment ? "" : houseId,
          apartments: [],
          units: [],
          unitDetails: {},
        };
      }

      if (shouldShowApartment) {
        acc[streetIdValue].houseDetails[houseNumber].apartments.push({
          houseId,
          name: apartmentType,
        });
      } else if (!acc[streetIdValue].houseDetails[houseNumber].houseId) {
        acc[streetIdValue].houseDetails[houseNumber].houseId = houseId;
      }

      if (shouldShowUnit && !acc[streetIdValue].houseDetails[houseNumber].units.includes(unit)) {
        acc[streetIdValue].houseDetails[houseNumber].units.push(unit);
        acc[streetIdValue].houseDetails[houseNumber].unitDetails[unit] = {
          houseId,
          houseNumber,
          unit,
        };
      }

      return acc;
    }, {});

    // Sort house numbers for each street
    Object.values(transformedData).forEach(street => {
      street.houses.sort((a, b) => {
        // Try to sort numerically first
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        // Fall back to string sorting
        return String(a).localeCompare(String(b));
      });

      Object.values(street.houseDetails).forEach((house) => {
        house.apartments.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      });
    });

    console.log("Transformed street data:", transformedData);
    setStreetsData(transformedData);
    setLoadingStreets(false);
  } catch (error) {
    console.error("Error in fetchStreetsData:", error);
    setLoadingStreets(false);
    errorNotifier(error.response?.data?.result?.message || "Failed to load streets data");
  }
};

export const fetchHouseUnitsData = async (
  houseId,
  setApartmentUnits,
  setLoadingHouseUnits
) => {
  try {
    setLoadingHouseUnits(true);
    const { data } = await http.get(
      `/user/house-units?houseId=${encodeURIComponent(houseId)}`
    );
    const houseUnits = data?.result?.data?.houseUnits;

    if (
      data?.result?.success === false &&
      data?.result?.message === "House unit list is empty"
    ) {
      setApartmentUnits({});
      return;
    }

    if (!Array.isArray(houseUnits)) {
      throw new Error("Invalid house units response");
    }

    const unitsByApartment = houseUnits.reduce((acc, unit) => {
      const apartment = unit?.houseId?.apartment;
      const unitName = unit?.name;

      if (!apartment || !unitName || String(unitName).toUpperCase() === "EMPTY") {
        return acc;
      }

      if (!acc[apartment]) {
        acc[apartment] = [];
      }

      if (!acc[apartment].some((item) => item.name === unitName)) {
        acc[apartment].push({
          id: unit._id,
          name: unitName,
        });
      }

      return acc;
    }, {});

    setApartmentUnits(unitsByApartment);
  } catch (error) {
    if (error.response?.data?.result?.message === "House unit list is empty") {
      setApartmentUnits({});
      return;
    }

    errorNotifier(
      error.response?.data?.result?.message || "Failed to load apartment units"
    );
    setApartmentUnits({});
  } finally {
    setLoadingHouseUnits(false);
  }
};



export const signupTenant = async (payload, setLoading) => {
  try {
    setLoading(true);
    const { data } = await http.post(AUTH_ROUTES.SIGNUP, payload);

    if (data.result.success) {
        successNotifier(data.result?.message);
        return true; 
    } else {
      errorNotifier(data.result?.message || "Signup failed. Please try again.");
      return false; 
    }
  } catch (e) {
    const resultMessage = e?.response?.data?.result?.message;
    const message = e?.response?.data?.message;

    if (Array.isArray(e?.response?.data?.errors)) {
        const errorMessages = e.response.data.errors
          .map((error) => error.msg)
          .join(", ");
        errorNotifier(errorMessages || "Signup failed with errors.");
      } else if (resultMessage || message) {
        errorNotifier(resultMessage || message);
      } else {
        errorNotifier("Please check your network connection!!!");
      }
  } finally {
    setLoading(false);
  }
};
